import * as React from 'react';

/**
 * Infinite-scroll sentinel (IntersectionObserver), shared across offer-tab
 * cursor-paginated tables (units tab, pricing tab, availability tab) and the
 * client offer view's infinite-scroll unit list.
 *
 * Returns a callback ref to attach to a sentinel element placed at the bottom
 * of the scroll container. When the sentinel intersects the viewport and a
 * further page exists, `fetchNextPage` is called. This drives infinite scroll
 * over the existing (non-virtualized) `DataTable` WITHOUT row virtualization
 * (an explicit Non-Goal of the pagination changes that introduced it).
 *
 * `sentinelRef` is a callback ref, not a `useRef`-based object, so it
 * re-attaches automatically whenever the underlying DOM node changes —
 * including when the same `sentinelRef` is handed to two different
 * conditionally-mounted elements (e.g. a map view and a table view toggled by
 * the consumer) and React swaps which physical node is mounted. A
 * `useRef`-based ref would leave a stale `IntersectionObserver` watching the
 * now-detached old node forever, since mutating `.current` doesn't re-run
 * effects. The callback ref fires on every mount (with the new node) and
 * unmount (with `null`), so the observer is torn down and recreated in step
 * with the DOM.
 */
export function useScrollSentinel(params: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}): { sentinelRef: (node: HTMLDivElement | null) => void } {
  const paramsRef = React.useRef(params);
  paramsRef.current = params;

  const observerRef = React.useRef<IntersectionObserver | null>(null);

  const sentinelRef = React.useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const { hasNextPage, isFetchingNextPage, fetchNextPage } = paramsRef.current;
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '0px 0px 30% 0px' },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return { sentinelRef };
}
